import { Button, Input, Textarea } from "@qualtio-workhub/ui";
import { Pencil } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useI18n } from "../../shared/i18n/provider";

type InlineField = "title" | "description" | "dueDate" | "estimate";

export function IssueInlineField({
  field,
  value,
  displayValue = value,
  editable,
  disabled = false,
  onSave,
}: {
  field: InlineField;
  value: string;
  displayValue?: ReactNode;
  editable: boolean;
  disabled?: boolean;
  onSave: (value: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const id = useId();
  const label = t(`issue.field.${field}`);
  const editLabel = `${t("issue.inline.edit")} ${label.toLocaleLowerCase()}`;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const editor = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const restoreFocus = useRef(false);
  const saveLock = useRef(false);
  const isDocument = field === "title" || field === "description";

  useEffect(() => {
    if (!editing && restoreFocus.current) {
      trigger.current?.focus({ preventScroll: true });
      restoreFocus.current = false;
    }
  }, [editing]);

  useEffect(() => {
    if (error && !saving && !disabled) editor.current?.focus({ preventScroll: true });
  }, [error, saving, disabled]);

  function close() {
    restoreFocus.current = true;
    setEditing(false);
    setError(null);
  }

  function begin() {
    setDraft(value);
    setError(null);
    setEditing(true);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saveLock.current || disabled) return;
    const nextDraft = editor.current?.value ?? draft;
    setDraft(nextDraft);
    if (field === "title" && !nextDraft.trim()) {
      setError(t("issue.inline.titleRequired"));
      return;
    }
    if (field === "dueDate" && editor.current?.validity.badInput) {
      setError(t("issue.inline.dateInvalid"));
      return;
    }
    if (
      field === "estimate" &&
      (editor.current?.validity.badInput ||
        (nextDraft !== "" && (!Number.isSafeInteger(Number(nextDraft)) || Number(nextDraft) < 0)))
    ) {
      setError(t("issue.inline.estimateInvalid"));
      return;
    }
    if (nextDraft.trim() === value.trim()) {
      close();
      return;
    }
    saveLock.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(nextDraft.trim());
      close();
    } catch {
      setError(t("issue.edit.error"));
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  }

  const display = editable ? (
    isDocument ? (
      <button
        aria-describedby={`${id}-value`}
        aria-label={editLabel}
        className="issue-text-edit-trigger"
        disabled={disabled}
        onClick={begin}
        ref={trigger}
        type="button"
      >
        <span id={`${id}-value`}>{displayValue}</span>
        <Pencil aria-hidden="true" className="issue-edit-pencil" size={14} />
      </button>
    ) : (
      <Button
        aria-describedby={`${id}-value`}
        aria-label={editLabel}
        disabled={disabled}
        onClick={begin}
        ref={trigger}
        size="sm"
        variant="ghost"
      >
        <span className="issue-property-value-text" id={`${id}-value`}>
          {displayValue}
        </span>
        <Pencil aria-hidden="true" size={12} />
      </Button>
    )
  ) : (
    <span>{displayValue}</span>
  );

  return (
    <div
      className={`issue-inline-field issue-inline-${field}${!isDocument ? " issue-property-edit" : ""}`}
    >
      {field === "title" ? (
        <h1 aria-label={value} className={editing ? "sr-only" : undefined}>
          {editing ? value : display}
        </h1>
      ) : null}
      {!isDocument ? <span className="issue-property-label">{label}</span> : null}
      {!editing && field !== "title" ? (
        <div className={isDocument ? "issue-document-description" : "issue-property-value"}>
          {display}
        </div>
      ) : null}
      {editing ? (
        <form
          aria-label={editLabel}
          className="issue-inline-form"
          noValidate
          onSubmit={(event) => void submit(event)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && !saving) {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
            if (event.key === "Enter" && event.nativeEvent.isComposing) event.preventDefault();
            if (
              field === "description" &&
              event.key === "Enter" &&
              (event.metaKey || event.ctrlKey) &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              event.currentTarget.requestSubmit();
            }
          }}
        >
          {field === "description" ? (
            <Textarea
              aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`}
              aria-invalid={Boolean(error)}
              aria-label={label}
              autoFocus
              disabled={saving || disabled}
              id={id}
              ref={(element) => {
                editor.current = element;
              }}
              value={draft}
              onChange={(event) => setDraft(event.currentTarget.value)}
            />
          ) : (
            <Input
              aria-describedby={`${id}-hint${error ? ` ${id}-error` : ""}`}
              aria-invalid={Boolean(error)}
              aria-label={label}
              autoFocus
              disabled={saving || disabled}
              id={id}
              ref={(element) => {
                editor.current = element;
              }}
              min={field === "estimate" ? 0 : undefined}
              step={field === "estimate" ? 1 : undefined}
              type={
                field === "estimate" ? "number" : field === "dueDate" ? "datetime-local" : "text"
              }
              value={draft}
              onFocus={(event) => {
                if (field === "title") event.currentTarget.select();
              }}
              onChange={(event) => setDraft(event.currentTarget.value)}
            />
          )}
          <small className="muted-copy" id={`${id}-hint`}>
            {t(
              field === "description"
                ? "issue.inline.descriptionHint"
                : field === "dueDate"
                  ? "issue.inline.dateHint"
                  : "issue.inline.fieldHint",
            )}
          </small>
          {error ? (
            <p className="error-text" id={`${id}-error`} role="alert">
              {error}
            </p>
          ) : null}
          <div className="issue-inline-actions">
            <Button disabled={saving || disabled} size="sm" type="submit">
              {t(saving ? "common.saving" : "common.save")}
            </Button>
            <Button disabled={saving} onClick={close} size="sm" variant="ghost">
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
